import { useMemo, useState } from 'react';
import useErrors from '../../../../hooks/useErrors';
import useAffreightmentsTab from './functions/useAffreightmentsTab';
import useApiIntegrationsTab from './functions/useApiIntegrationsTab';
import useExtractConsultEmails from './functions/useExtractConsultEmails';
import useFinancialTab from './functions/useFinancialTab';
import useGeneralTab from './functions/useGeneralTab';
import useLoadSettings from './functions/useLoadSettings';
import useLogoTab from './functions/useLogoTab';
import useMyLinkTab from './functions/useMyLinkTab';
import useRoutingAcceptedEmails from './functions/useRoutingAcceptedEmails';
import useRoutingNoResultEmails from './functions/useRoutingNoResultEmails';
import useRoutingRulesTab from './functions/useRoutingRulesTab';
import useSettingsSubmit from './functions/useSettingsSubmit';
import useTabSelections from './functions/useTabSelections';
import useVtItensRelationsRequestEmails from './functions/useVtItensRelationsRequestEmails';
import useFinancialEmails from './functions/useFinancialEmails';

export default function useSettings() {
  const [imageFile, setImageFile] = useState<File | string | undefined>();

  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();

  const {
    userProfile,
    activeTab,
    handleChangeActiveTab,
    companyName,
  } = useTabSelections();

  const {
    setAddExtractEmailModalShow,
    hasExtractEmailsToBeRendered,
    extractEmails,
    setExtractEmails,
    setExtractEmailToRemove,
    setRemoveExtractEmailModalShow,
    extractEmailToRemove,
    removeExtractEmail,
    addExtractEmailModalShow,
    extractEmailToAdd,
    handleExtractEmailToAddChange,
    addExtractEmail,
    removeExtractEmailModalShow,
    resetExtractEmails,
    hasOriginalExtractEmailsChanged,
    setOriginalExtractEmails,
    extractEmailOwnerNameToAdd,
    handleExtractEmailOwnerNameToAddChange,
  } = useExtractConsultEmails();

  const {
    setAddRoutingEmailModalShow,
    hasRoutingEmailsToBeRendered,
    routingEmails,
    setRoutingEmails,
    setRoutingEmailToRemove,
    setRemoveRoutingEmailModalShow,
    routingEmailToRemove,
    removeRoutingEmail,
    addRoutingEmailModalShow,
    routingEmailToAdd,
    handleRoutingEmailToAddChange,
    addRoutingEmail,
    removeRoutingEmailModalShow,
    resetRoutingEmails,
    hasOriginalRoutingEmailsChanged,
    setOriginalRoutingEmails,
    routingEmailOwnerNameToAdd,
    handleRoutingEmailOwnerNameToAddChange,
  } = useRoutingAcceptedEmails();

  const {
    setAddRoutingNoResultEmailModalShow,
    hasRoutingNoResultEmailsToBeRendered,
    routingNoResultEmails,
    setRoutingNoResultEmailToRemove,
    setRemoveRoutingNoResultEmailModalShow,
    routingNoResultEmailToRemove,
    removeRoutingNoResultEmail,
    addRoutingNoResultEmailModalShow,
    routingNoResultEmailToAdd,
    handleRoutingNoResultEmailToAddChange,
    addRoutingNoResultEmail,
    removeRoutingNoResultEmailModalShow,
    resetRoutingNoResultEmails,
    hasOriginalRoutingNoResultEmailsChanged,
    routingNoResultEmailOwnerNameToAdd,
    handleRoutingNoResultEmailOwnerNameToAddChange,
    setRoutingNoResultEmails,
    setOriginalRoutingNoResultEmails,
  } = useRoutingNoResultEmails();

  const {
    vtItensRelationsRequestEmailOwnerNameToAdd,
    handleVtItensRelationsRequestEmailOwnerNameToAddChange,
    addVtItensRelationsRequestEmail,
    removeVtItensRelationsRequestEmailModalShow,
    resetVtItensRelationsRequestEmails,
    hasOriginalVtItensRelationsRequestEmailsChanged,
    vtItensRelationsRequestEmails,
    vtItensRelationsRequestEmailToAdd,
    handleVtItensRelationsRequestEmailToAddChange,
    vtItensRelationsRequestEmailToRemove,
    setVtItensRelationsRequestEmailToRemove,
    setRemoveVtItensRelationsRequestEmailModalShow,
    removeVtItensRelationsRequestEmail,
    addVtItensRelationsRequestEmailModalShow,
    setAddVtItensRelationsRequestEmailModalShow,
    hasVtItensRelationsRequestEmailsToBeRendered,
    setOriginalVtItensRelationsRequestEmails,
    setVtItensRelationsRequestEmails,
  } = useVtItensRelationsRequestEmails();

  const {
    setAddFinancialEmailModalShow,
    hasFinancialEmailsToBeRendered,
    financialEmails,
    setFinancialEmails,
    setFinancialEmailToRemove,
    setRemoveFinancialEmailModalShow,
    financialEmailToRemove,
    removeFinancialEmail,
    addFinancialEmailModalShow,
    financialEmailToAdd,
    handleFinancialEmailToAddChange,
    addFinancialEmail,
    removeFinancialEmailModalShow,
    resetFinancialEmails,
    hasOriginalFinancialEmailsChanged,
    setOriginalFinancialEmails,
    financialEmailOwnerNameToAdd,
    handleFinancialEmailOwnerNameToAddChange,
  } = useFinancialEmails();

  const {
    willIntegrateWithPayroll,
    setWillIntegrateWithPayroll,
    technology,
    setTechnology,
    technologyOptions,
    setTechnologyOptions,
    isApiIntegrationsTabValid,
    hasWebhook,
    setHasWebhook,
    hasResultReadyWebhook,
    setHasResultReadyWebhook,
    hasResultOutOfScopeWebhook,
    setHasResultOutOfScopeWebhook,
    integrator,
    setIntegrator,
  } = useApiIntegrationsTab();

  const {
    losesOwnershipWithoutBoarding,
    setLosesOwnershipWithoutBoarding,
    daysAllowedWithoutBoarding,
    setDaysAllowedWithoutBoarding,
    isAffreightmentsTabValid,
    handleDaysAllowedWithoutBoardingChange,
    handleLosesOwnershipWithoutBoardingChange,
    usaHorarioFretado,
    setUsaHorarioFretado,
  } = useAffreightmentsTab({ setError, removeError });

  const {
    sendAnswerOfClaimToCollabEmail,
    setSendAnswerOfClaimToCollabEmail,
    allowCollabToSeeLetter,
    setAllowCollabToSeeLetter,
    allowMultipleConsults,
    setAllowMultipleConsults,
    usesCustomLogo,
    setUsesCustomLogo,
    integrateWithWs,
    setIntegrateWithWs,
    isMyLinkFull,
    setIsMyLinkFull,
    verifySalaryRJ,
    setVerifySalaryRJ,
    mustSendAddressProof,
    setMusStendAddressProof: setMustSendAddressProof,
  } = useMyLinkTab();

  const {
    vtProvider,
    setVtProvider,
    vtProviderOptions,
    setVtProviderOptions,
    vtPurchaseProvider,
    setVtPurchaseProvider,
    hasCustomLogo,
    setHasCustomLogo,
    showTicketsAtNotOptimizedResults,
    setShowTicketsAtNotOptimizedResults,
    hasCustomTextAtLetter,
    setHasCustomTextAtLetter,
    handleHasCustomTextAtLetterChange,
    customTextAtLetter,
    setCustomTextAtLetter,
    handleCustomTextAtLetterChange,
    partner,
    setPartner,
    partnerOptions,
    setPartnerOptions,
    secondPartner,
    setSecondPartner,
    secondPartnerOptions,
    directResult,
    setDirectResult,
    maxContestsAllowed,
    setMaxContestsAllowed,
    isGeneralTabValid,
    defaultWorkplaceUsesIntegrationWs,
    setDefaultWorkplaceUsesIntegrationWs,
    changesWpDataByWs,
    setChangesWpDataByWs,
    isPartner,
    setIsPartner,
    usesLogoAtEmail,
    setUsesLogoAtEmail,
    canRequestVtItemRelation,
    setCanRequestVtItemRelation,
    particularities,
    setParticularities,
  } = useGeneralTab({ setError, removeError });

  const {
    geraNFSeparada,
    setGeraNFSeparada,
  } = useFinancialTab();

  const {
    copyGoingToReturn,
    setCopyGoingToReturn,
    hasAffreightment,
    setHasAffreightment,
    hasKmLimit,
    setHasKmLimit,
    selectedKmLimit,
    setSelectedKmLimit,
    kmLimit,
    setKmLimit,
    selectedWalkDistance,
    setSelectedWalkDistance,
    walkDistance,
    setWalkDistance,
    transfersBehavior,
    setTransfersBehavior,
    compareWithDesiredVtValue,
    setCompareWithDesiredVtValue,
    utilizaTetoValorPadraoEmp,
    setUtilizaTetoValorPadraoEmp,
    desiredVtValue,
    setDesiredVtValue,
    chooseDefaultValueForRoutings,
    setChooseDefaultValueForRoutings,
    defaultValueForRoutings,
    setDefaultValueForRoutings,
    kindOfVtSpTrans,
    setKindOfVtSpTrans,
    handleKmLimitSelection,
    handleWalkDistanceSelection,
    handleCompareWithDesiredVtValueChange,
    handleDesiredVtValueChange,
    handleChooseDefaultValueForRoutingsChange,
    handleDefaultValueForRoutingsChange,
    isRoutingRulesTabValid,
    kindOfVtAesa,
    setKindOfVtAesa,
    aboveDesiredValueReturnsBiasTag,
    setAboveDesiredValueReturnsBiasTag,
    usaRodoviarioValZero,
    setUsaRodoviarioValZero,
    handleUsaRodoviarioValZeroChange,
    kmRodoviarioValZero,
    setKmRodoviarioValZero,
    handleKmRodoviarioValZeroChange,
  } = useRoutingRulesTab({ setError, removeError });

  const { isLoadLoading, loadPage, isFirstSetup, tabsDisabled, currentServices, geraNFSeparadaMatriz } = useLoadSettings({
    setCopyGoingToReturn,
    setHasAffreightment,
    setUsaHorarioFretado,
    setHasKmLimit,
    setKmLimit,
    setWalkDistance,
    setTransfersBehavior,
    setCompareWithDesiredVtValue,
    setUtilizaTetoValorPadraoEmp,
    setDesiredVtValue,
    setChooseDefaultValueForRoutings,
    setDefaultValueForRoutings,
    setKindOfVtSpTrans,
    setVtProvider,
    setVtProviderOptions,
    setVtPurchaseProvider,
    setHasCustomLogo,
    setShowTicketsAtNotOptimizedResults,
    setHasCustomTextAtLetter,
    setCustomTextAtLetter,
    setPartner,
    setPartnerOptions,
    setSecondPartner,
    setDirectResult,
    setMaxContestsAllowed,
    setSendAnswerOfClaimToCollabEmail,
    setAllowCollabToSeeLetter,
    setAllowMultipleConsults,
    setUsesCustomLogo,
    setIntegrateWithWs,
    setLosesOwnershipWithoutBoarding,
    setDaysAllowedWithoutBoarding,
    setWillIntegrateWithPayroll,
    setTechnology,
    setTechnologyOptions,
    setRoutingNoResultEmails,
    setOriginalRoutingNoResultEmails,
    setRoutingEmails,
    setOriginalRoutingEmails,
    setExtractEmails,
    setOriginalExtractEmails,
    setSelectedKmLimit,
    setSelectedWalkDistance,
    setImageFile,
    setIsMyLinkFull,
    setDefaultWorkplaceUsesIntegrationWs,
    setChangesWpDataByWs,
    setIsPartner,
    setUsesLogoAtEmail,
    setVerifySalaryRJ,
    setVtItensRelationsRequestEmails,
    setOriginalVtItensRelationsRequestEmails,
    setFinancialEmails,
    setOriginalFinancialEmails,
    setCanRequestVtItemRelation,
    setHasWebhook,
    setKindOfVtAesa,
    setAboveDesiredValueReturnsBiasTag,
    setUsaRodoviarioValZero,
    setKmRodoviarioValZero,
    setHasResultReadyWebhook,
    setHasResultOutOfScopeWebhook,
    setIntegrator,
    setParticularities,
    setMustSendAddressProof,
    setGeraNFSeparada,
  });

  const {
    setupCompany,
    isSubmitingCompanySettings,
  } = useSettingsSubmit({
    isFirstSetup,
    extractEmails,
    routingEmails,
    routingNoResultEmails,
    vtItensRelationsRequestEmails,
    financialEmails,
    canRequestVtItemRelation,
    willIntegrateWithPayroll,
    technology,
    losesOwnershipWithoutBoarding,
    daysAllowedWithoutBoarding,
    sendAnswerOfClaimToCollabEmail,
    allowCollabToSeeLetter,
    allowMultipleConsults,
    usesCustomLogo,
    integrateWithWs,
    vtProvider,
    vtPurchaseProvider,
    hasCustomLogo,
    showTicketsAtNotOptimizedResults,
    hasCustomTextAtLetter,
    customTextAtLetter,
    partner,
    directResult,
    maxContestsAllowed,
    copyGoingToReturn,
    hasAffreightment,
    usaHorarioFretado,
    hasKmLimit,
    kmLimit,
    walkDistance,
    transfersBehavior,
    compareWithDesiredVtValue,
    desiredVtValue,
    utilizaTetoValorPadraoEmp,
    chooseDefaultValueForRoutings,
    defaultValueForRoutings,
    kindOfVtSpTrans,
    handleChangeActiveTab,
    imageFile,
    loadPage,
    currentServices,
    isMyLinkFull,
    defaultWorkplaceUsesIntegrationWs,
    changesWpDataByWs,
    isPartner,
    secondPartner,
    usesLogoAtEmail,
    verifySalaryRJ,
    hasWebhook,
    kindOfVtAesa,
    aboveDesiredValueReturnsBiasTag,
    hasResultReadyWebhook,
    hasResultOutOfScopeWebhook,
    integrator,
    particularities,
    mustSendAddressProof,
    usaRodoviarioValZero,
    kmRodoviarioValZero,
    geraNFSeparada,
  });

  const { uploadImage, isUploadingImage } = useLogoTab({ loadPage, imageFile, setupCompany });

  const isLoading = isLoadLoading || isUploadingImage || isSubmitingCompanySettings;
  const areSettingsValid = useMemo(() => (!!
  (
    isGeneralTabValid &&
    isRoutingRulesTabValid &&
    isAffreightmentsTabValid &&
    isApiIntegrationsTabValid &&
    errors.length === 0
  )), [errors.length, isAffreightmentsTabValid, isApiIntegrationsTabValid, isGeneralTabValid, isRoutingRulesTabValid]);

  return {
    userProfile,
    activeTab,
    handleChangeActiveTab,
    companyName,
    setAddExtractEmailModalShow,
    hasExtractEmailsToBeRendered,
    extractEmails,
    setExtractEmails,
    setExtractEmailToRemove,
    setRemoveExtractEmailModalShow,
    extractEmailToRemove,
    removeExtractEmail,
    addExtractEmailModalShow,
    extractEmailToAdd,
    handleExtractEmailToAddChange,
    addExtractEmail,
    removeExtractEmailModalShow,
    resetExtractEmails,
    hasOriginalExtractEmailsChanged,
    setOriginalExtractEmails,
    setAddRoutingNoResultEmailModalShow,
    hasRoutingNoResultEmailsToBeRendered,
    routingNoResultEmails,
    setRoutingNoResultEmailToRemove,
    setRemoveRoutingNoResultEmailModalShow,
    routingNoResultEmailToRemove,
    removeRoutingNoResultEmail,
    addRoutingNoResultEmailModalShow,
    routingNoResultEmailToAdd,
    handleRoutingNoResultEmailToAddChange,
    addRoutingNoResultEmail,
    removeRoutingNoResultEmailModalShow,
    resetRoutingNoResultEmails,
    hasOriginalRoutingNoResultEmailsChanged,
    setAddRoutingEmailModalShow,
    hasRoutingEmailsToBeRendered,
    routingEmails,
    setRoutingEmails,
    setRoutingEmailToRemove,
    setRemoveRoutingEmailModalShow,
    routingEmailToRemove,
    removeRoutingEmail,
    addRoutingEmailModalShow,
    routingEmailToAdd,
    handleRoutingEmailToAddChange,
    addRoutingEmail,
    removeRoutingEmailModalShow,
    resetRoutingEmails,
    hasOriginalRoutingEmailsChanged,
    setOriginalRoutingEmails,
    extractEmailOwnerNameToAdd,
    handleExtractEmailOwnerNameToAddChange,
    routingEmailOwnerNameToAdd,
    handleRoutingEmailOwnerNameToAddChange,
    routingNoResultEmailOwnerNameToAdd,
    handleRoutingNoResultEmailOwnerNameToAddChange,
    willIntegrateWithPayroll,
    setWillIntegrateWithPayroll,
    technology,
    setTechnology,
    technologyOptions,
    setTechnologyOptions,
    isApiIntegrationsTabValid,
    losesOwnershipWithoutBoarding,
    setLosesOwnershipWithoutBoarding,
    daysAllowedWithoutBoarding,
    handleDaysAllowedWithoutBoardingChange,
    getErrorMessageByFieldName,
    errors,
    setDaysAllowedWithoutBoarding,
    isAffreightmentsTabValid,
    sendAnswerOfClaimToCollabEmail,
    setSendAnswerOfClaimToCollabEmail,
    allowCollabToSeeLetter,
    setAllowCollabToSeeLetter,
    allowMultipleConsults,
    setAllowMultipleConsults,
    usesCustomLogo,
    setUsesCustomLogo,
    integrateWithWs,
    setIntegrateWithWs,
    handleLosesOwnershipWithoutBoardingChange,
    usaHorarioFretado,
    setUsaHorarioFretado,
    vtProvider,
    setVtProvider,
    vtProviderOptions,
    setVtProviderOptions,
    vtPurchaseProvider,
    setVtPurchaseProvider,
    hasCustomLogo,
    setHasCustomLogo,
    showTicketsAtNotOptimizedResults,
    setShowTicketsAtNotOptimizedResults,
    hasCustomTextAtLetter,
    setHasCustomTextAtLetter,
    handleHasCustomTextAtLetterChange,
    customTextAtLetter,
    setCustomTextAtLetter,
    handleCustomTextAtLetterChange,
    partner,
    setPartner,
    partnerOptions,
    setPartnerOptions,
    secondPartner,
    setSecondPartner,
    secondPartnerOptions,
    directResult,
    setDirectResult,
    maxContestsAllowed,
    setMaxContestsAllowed,
    isGeneralTabValid,
    copyGoingToReturn,
    setCopyGoingToReturn,
    hasAffreightment,
    setHasAffreightment,
    hasKmLimit,
    setHasKmLimit,
    selectedKmLimit,
    setSelectedKmLimit,
    kmLimit,
    setKmLimit,
    selectedWalkDistance,
    setSelectedWalkDistance,
    walkDistance,
    setWalkDistance,
    transfersBehavior,
    setTransfersBehavior,
    compareWithDesiredVtValue,
    setCompareWithDesiredVtValue,
    utilizaTetoValorPadraoEmp,
    setUtilizaTetoValorPadraoEmp,
    desiredVtValue,
    setDesiredVtValue,
    chooseDefaultValueForRoutings,
    setChooseDefaultValueForRoutings,
    defaultValueForRoutings,
    setDefaultValueForRoutings,
    kindOfVtSpTrans,
    setKindOfVtSpTrans,
    handleKmLimitSelection,
    handleWalkDistanceSelection,
    handleCompareWithDesiredVtValueChange,
    handleDesiredVtValueChange,
    handleChooseDefaultValueForRoutingsChange,
    handleDefaultValueForRoutingsChange,
    isRoutingRulesTabValid,
    isLoading,
    uploadImage,
    imageFile,
    setImageFile,
    setupCompany,
    areSettingsValid,
    tabsDisabled,
    setIsMyLinkFull,
    isMyLinkFull,
    defaultWorkplaceUsesIntegrationWs,
    setDefaultWorkplaceUsesIntegrationWs,
    changesWpDataByWs,
    setChangesWpDataByWs,
    isPartner,
    setIsPartner,
    usesLogoAtEmail,
    setUsesLogoAtEmail,
    verifySalaryRJ,
    setVerifySalaryRJ,
    vtItensRelationsRequestEmailOwnerNameToAdd,
    handleVtItensRelationsRequestEmailOwnerNameToAddChange,
    addVtItensRelationsRequestEmail,
    removeVtItensRelationsRequestEmailModalShow,
    resetVtItensRelationsRequestEmails,
    hasOriginalVtItensRelationsRequestEmailsChanged,
    vtItensRelationsRequestEmails,
    vtItensRelationsRequestEmailToAdd,
    handleVtItensRelationsRequestEmailToAddChange,
    vtItensRelationsRequestEmailToRemove,
    setVtItensRelationsRequestEmailToRemove,
    setRemoveVtItensRelationsRequestEmailModalShow,
    removeVtItensRelationsRequestEmail,
    addVtItensRelationsRequestEmailModalShow,
    setAddVtItensRelationsRequestEmailModalShow,
    hasVtItensRelationsRequestEmailsToBeRendered,
    financialEmailOwnerNameToAdd,
    handleFinancialEmailOwnerNameToAddChange,
    addFinancialEmail,
    removeFinancialEmailModalShow,
    resetFinancialEmails,
    hasOriginalFinancialEmailsChanged,
    financialEmails,
    financialEmailToAdd,
    handleFinancialEmailToAddChange,
    financialEmailToRemove,
    setFinancialEmailToRemove,
    setRemoveFinancialEmailModalShow,
    removeFinancialEmail,
    addFinancialEmailModalShow,
    setAddFinancialEmailModalShow,
    hasFinancialEmailsToBeRendered,
    canRequestVtItemRelation,
    setCanRequestVtItemRelation,
    hasWebhook,
    setHasWebhook,
    hasResultReadyWebhook,
    setHasResultReadyWebhook,
    kindOfVtAesa,
    setKindOfVtAesa,
    aboveDesiredValueReturnsBiasTag,
    setAboveDesiredValueReturnsBiasTag,
    hasResultOutOfScopeWebhook,
    setHasResultOutOfScopeWebhook,
    integrator,
    setIntegrator,
    setParticularities,
    particularities,
    mustSendAddressProof,
    setMustSendAddressProof,
    usaRodoviarioValZero,
    setUsaRodoviarioValZero,
    handleUsaRodoviarioValZeroChange,
    kmRodoviarioValZero,
    setKmRodoviarioValZero,
    handleKmRodoviarioValZeroChange,
    geraNFSeparada,
    setGeraNFSeparada,
    geraNFSeparadaMatriz,
  };
}
